import { AccessTemplateType } from '@api/company';
import { CLIENT_USER_ROLE, CLIENT_USER_STATUS } from '@api/types/client-user-management';
import { PVX_PARTNERS_TYPE } from '@api/types/pvxPartners';
import { PendingSignupStatus } from '@api/pendingSignups';
import type { UserRole } from './types/userDetailsForm';
import { UserFormRow } from './types/userDetailsForm';
import { USER_STATUS, USER_STATUS_MAP } from '@constants/constants';

export const USER_MANAGEMENT_PAGE_SIZE = 10;
export const USER_MANAGEMENT_PAGE_NUMBER = 0;

export enum UserManagementTabs {
  CLIENTS = 'CLIENTS',
  PARTNERS = 'PARTNERS',
  PENDING = 'PENDING',
  ALL = 'ALL',
}

export enum UserManagementScreen {
  LISTING = 'listing',
  EDIT_COMPANY = 'editCompany',
  ADD_PVX_PARTNER = 'addPvxPartner',
  EDIT_PVX_PARTNER = 'editPvxPartner',
  ADD_PVX_USER = 'addPvxUser',
  EDIT_PVX_USER = 'editPvxUser',
  CLONE_PVX_USER = 'clonePvxUser',
}

export enum PartnerEntityType {
  COMPANY = 'COMPANY',
  INDIVIDUAL = 'INDIVIDUAL',
}

//User form
export const DEFAULT_USER_ROW: UserFormRow = { email: '', username: '', userRole: 'VIEWER' };

// Role filter constants — Clients tab
export const CLIENT_ROLE_DISPLAY_LABELS: Record<CLIENT_USER_ROLE, string> = {
  [CLIENT_USER_ROLE.OWNER]: 'Owner',
  [CLIENT_USER_ROLE.FULL_MEMBER]: 'Full Member',
  [CLIENT_USER_ROLE.MEMBER]: 'Member',
};

export const CLIENT_ROLE_FILTER_OPTIONS = Object.values(CLIENT_USER_ROLE).map((role) => ({
  label: CLIENT_ROLE_DISPLAY_LABELS[role],
  value: role,
}));

// Role filter constants — All tab
export const PVX_ROLE_DISPLAY_LABELS: Record<UserRole, string> = {
  VIEWER: 'Viewer',
  ADMIN: 'Admin',
};

const PVX_ROLES: UserRole[] = ['VIEWER', 'ADMIN'];

export const PVX_ROLE_FILTER_OPTIONS = PVX_ROLES.map((role) => ({
  label: PVX_ROLE_DISPLAY_LABELS[role],
  value: role,
}));

export const CLIENT_STATUS_DISPLAY_LABELS: Record<CLIENT_USER_STATUS, string> = {
  [CLIENT_USER_STATUS.ACTIVE]: 'Active',
  [CLIENT_USER_STATUS.INVITED]: 'Invited',
  [CLIENT_USER_STATUS.PENDING_DELETION]: 'Pending Deletion',
  [CLIENT_USER_STATUS.REVOKED]: 'Revoked',
};

export const CLIENT_STATUS_FILTER_OPTIONS = Object.values(CLIENT_USER_STATUS).map((status) => ({
  label: CLIENT_STATUS_DISPLAY_LABELS[status],
  value: status,
}));

export const STATUS_FILTER_OPTIONS = Object.values(USER_STATUS).map((status) => ({
  label: USER_STATUS_MAP[status as USER_STATUS],
  value: status,
}));

// Partner type filter constants — Partners tab
export const PARTNER_TYPE_DISPLAY_LABELS: Record<PVX_PARTNERS_TYPE, string> = {
  [PVX_PARTNERS_TYPE.FINANCE]: 'Financing Partner',
  [PVX_PARTNERS_TYPE.LP]: 'Liquidity Provider',
  [PVX_PARTNERS_TYPE.REFERRER]: 'Referrer',
  [PVX_PARTNERS_TYPE.INVESTOR]: 'Investor',
  [PVX_PARTNERS_TYPE.OTHER]: 'Other',
};

export const PARTNER_TYPE_FILTER_OPTIONS = Object.values(PVX_PARTNERS_TYPE).map((type) => ({
  label: PARTNER_TYPE_DISPLAY_LABELS[type],
  value: type,
}));

// Excludes LP
export const PARTNER_TYPE_OPTIONS = Object.values(PVX_PARTNERS_TYPE)
  .filter((type) => type !== PVX_PARTNERS_TYPE.LP)
  .map((type) => ({
    label: PARTNER_TYPE_DISPLAY_LABELS[type],
    value: type,
  }));

export const PARTNER_ENTITY_TYPE_DISPLAY_LABELS: Record<string, string> = {
  [PartnerEntityType.COMPANY]: 'Company',
  [PartnerEntityType.INDIVIDUAL]: 'Individual',
};

export const PARTNER_ENTITY_TYPE_OPTIONS = Object.values(PartnerEntityType).map((type) => ({
  label: PARTNER_ENTITY_TYPE_DISPLAY_LABELS[type],
  value: type,
}));

// Pending signup status labels & filters
export const PENDING_SIGNUP_STATUS_LABELS: Record<PendingSignupStatus, string> = {
  [PendingSignupStatus.ONBOARDING_ELIGIBLE]: 'Awaiting action',
  [PendingSignupStatus.COMPANY_CREATION_REQUESTED]: 'New Company requested',
  [PendingSignupStatus.COMPANY_ACCESS_REQUESTED]: 'Company access requested',
};

export const PENDING_SIGNUP_STATUS_FILTER_OPTIONS = Object.values(PendingSignupStatus).map(
  (status) => ({
    label: PENDING_SIGNUP_STATUS_LABELS[status],
    value: status,
  }),
);

export const FEEDBACK_SUBMITTED_FILTER_OPTIONS = [
  { label: 'Raised', value: 'true' },
  { label: 'Not Raised', value: 'false' },
];

//User template
export const CUSTOM_TEMPLATE: AccessTemplateType = {
  internalUserAccessTemplateType: 'CUSTOM',
  label: 'Custom',
  submodules: [],
};
